'use client';

import { Box } from '@mui/material';
import { CircularProgress, Typography } from '@/primitives';
import { HyperLink } from '@/components/publicSearch/resultCell/HyperLink';
import type { useAnimalSaleHistory } from '@/hooks/publicSearch/animal/useAnimalSaleHistory';

type SaleHistory = ReturnType<typeof useAnimalSaleHistory>;

type AnimalSalesTabProps = {
  salesLoading: SaleHistory['loading'];
  salesError: SaleHistory['error'];
  activeSales: SaleHistory['activeSales'];
  previousSales: SaleHistory['previousSales'];
  activeLotNotes: SaleHistory['activeLotNotes'];
  /** Builds the public sale-listing href for a lot (carries search context). */
  buildSaleListingPath: (saleId: number, lotNo?: string) => string;
};

/**
 * Sales tab body for the public animal-detail page: the animal's current and
 * previous sale-lot listings plus the active lot's notes. The sale-history
 * query lives in the page (its counts also feed the tab badge), so this stays a
 * presentational component driven entirely by props.
 */
export default function AnimalSalesTab({
  salesLoading,
  salesError,
  activeSales,
  previousSales,
  activeLotNotes,
  buildSaleListingPath,
}: AnimalSalesTabProps) {
  if (salesLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography
          variant="bodysmall"
          sx={{
            color: 'text.secondary',
          }}
        >
          Loading sales…
        </Typography>
      </Box>
    );
  }

  if (salesError) {
    return (
      <Typography
        variant="bodysmall"
        sx={{
          color: 'text.secondary',
        }}
      >
        Unable to load sales.
      </Typography>
    );
  }

  if (activeSales.length === 0 && previousSales.length === 0) {
    return (
      <Typography
        variant="bodysmall"
        sx={{
          color: 'text.secondary',
        }}
      >
        No sales available.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
        gap: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          flex: 1,
          maxWidth: { md: 640 },
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          Currently listed in
        </Typography>
        {activeSales.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {activeSales.map((entry) => (
              <HyperLink
                key={`${entry.saleId}-${entry.lotNo}`}
                label={`Lot ${entry.lotNo} - Sale ${entry.saleName}`}
                pathname={buildSaleListingPath(entry.saleId, entry.lotNo)}
                linkStyle={{
                  padding: 0,
                  minWidth: 'min-content',
                  justifyContent: 'flex-start',
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography
            variant="bodysmall"
            sx={{
              color: 'text.secondary',
            }}
          >
            No active sales.
          </Typography>
        )}
        <Typography sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
          Previous Sales
        </Typography>
        {previousSales.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {previousSales.map((entry) => (
              <HyperLink
                key={`${entry.saleId}-${entry.lotNo}`}
                label={`Lot ${entry.lotNo} - Sale ${entry.saleName}`}
                pathname={buildSaleListingPath(entry.saleId, entry.lotNo)}
                linkStyle={{
                  padding: 0,
                  minWidth: 'min-content',
                  justifyContent: 'flex-start',
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography
            variant="bodysmall"
            sx={{
              color: 'text.secondary',
            }}
          >
            No previous sales.
          </Typography>
        )}
      </Box>
      {activeSales.length > 0 &&
        activeLotNotes &&
        activeLotNotes.trim() !== '' &&
        activeLotNotes.trim() !== '—' && (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              flex: 1,
              maxWidth: { md: 420 },
            }}
          >
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Lot Notes</Typography>
            <Typography
              variant="bodysmall"
              component="div"
              dangerouslySetInnerHTML={{
                __html: activeLotNotes,
              }}
              sx={{
                color: 'text.secondary',
                '& p': { m: 0 },
                '& p + p': { mt: 0.5 },
                '& ul, & ol': { pl: 2.5, m: 0 },
                '& a': { color: 'primary.main' },
              }}
            />
          </Box>
        )}
    </Box>
  );
}
